<template>
  <div class="create-consultation-wrapper">
    <div class="prescription-creation-section">
      <button type="button" @click="toggleDropdown" class="create-prescription-button">
        <i class="fas fa-plus-circle"></i> Create Prescriptions
      </button>

      <div v-if="dropdownOpen && products.length > 0" class="create-consultation-dropdown">
        <div class="dropdown-search">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="dropdown-search-input"
            placeholder="Type to filter products..."
            autocomplete="off"
            @click.stop
            @keydown.enter.prevent="selectFirstMatch"
          >
        </div>

        <div class="dropdown-list">
          <button
            type="button"
            v-for="product in filteredProducts"
            :key="product.id"
            @click="selectDrug(product.id)"
            class="dropdown-item-button"
          >
            {{ product.fullName }}
            <span v-if="product.strength" class="strength">{{ product.strength }}</span>
            <span class="quantity">{{ product.quantity }}</span>
          </button>
          <div v-if="filteredProducts.length === 0" class="dropdown-empty">
            No matching products
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-indicator">
      <p>Creating prescription...</p>
      <i class="fa fa-spinner fa-spin"></i>
    </div>
    <div v-if="creationError" class="formError">
      {{ creationError }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    consultationId: {
      type: Number,
    },
  },
  data() {
    return {
      dropdownOpen: false,
      loading: false,
      creationError: null,
      products: [],
      searchQuery: '',
    };
  },
  computed: {
    filteredProducts() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        return this.products;
      }

      return this.products.filter((product) => {
        const haystack = [
          product.fullName,
          product.name,
          product.strength,
          product.quantity,
          product.type && product.type.name,
          product.category && product.category.title,
        ].filter(Boolean).join(' ').toLowerCase();

        return haystack.indexOf(query) !== -1;
      });
    },
  },
  methods: {
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
      if (this.dropdownOpen) {
        this.creationError = null;
        this.searchQuery = '';
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
    selectDrug(drugId) {
      this.dropdownOpen = false;
      this.searchQuery = '';
      this.createPrescription(drugId);
    },
    selectFirstMatch() {
      if (this.filteredProducts.length > 0) {
        this.selectDrug(this.filteredProducts[0].id);
      }
    },
    handleClickOutside(event) {
      if (this.dropdownOpen && !this.$el.contains(event.target)) {
        this.dropdownOpen = false;
        this.searchQuery = '';
      }
    },
    createPrescription(drugId) {
      this.loading = true;
      this.creationError = null;

      axios.post('/api/v1/consultation/create/derma', {
        drugId: drugId,
        consultationId: this.consultationId,
      })
        .then(response => {
          location.reload();
        })
        .catch(error => {
          this.creationError = 'Failed to create prescription. Please try again.';
          if (error.response && error.response.data && error.response.data.message) {
            this.creationError = error.response.data.message;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getProducts() {
      axios.get('/api/v1/products')
        .then(response => {
          const data = response.data.data;
          this.products = data.filter(p => (
            !(p.category && p.category.slug === 'weight-lost')
          ));
        })
        .catch(error => {
          console.error('Error fetching products:', error);
        });
    },
  },
  mounted() {
    this.getProducts()
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.create-consultation-wrapper {
  display: inline-block;
  position: relative;
}

.prescription-creation-section {
  position: relative;
  display: inline-block;
}

.create-prescription-button {
  padding: 10px 15px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  background: #4867C0;
  color: #ffffff;
  border-radius: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s ease;
}

.create-prescription-button:hover {
  background-color: #334c94;
}

.create-consultation-dropdown {
  position: absolute;
  background-color: white;
  min-width: 280px;
  max-height: 360px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  border: 1px solid #ddd;
  border-radius: 4px;
  top: 100%;
  left: 0;
  margin-top: 5px;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.dropdown-search {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.dropdown-search-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.dropdown-search-input:focus {
  border-color: #4867C0;
}

.dropdown-list {
  overflow-y: auto;
  max-height: 300px;
}

.dropdown-empty {
  padding: 12px 16px;
  color: #888;
  font-size: 13px;
}

.dropdown-item-button {
  background: none;
  border: none;
  outline: none;
  color: #333;
  padding: 12px 16px;
  text-align: left;
  text-decoration: none;
  display: block;
  width: 100%;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.dropdown-item-button:hover {
  background-color: #f1f1f1;
  color: #333;
}

.loading-indicator {
  margin-top: 15px;
  color: #007bff;
  display: flex;
  align-items: center;
  gap: 8px;
}

.formError {
  color: red;
  margin-top: 10px;
  margin-bottom: 10px;
  font-weight: bold;
}

.strength {
  color: #666;
}

.quantity {
  background-color: #3686c7;
  font-size: 12px;
  color: #fff;
  padding: 2px 5px;
  border-radius: 5px;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/CreateConsultationForDerma.vue